// src/hooks/usePreventivo.js
import { useState, useEffect } from 'react';

const INITIAL_WINDOW = () => ({
  id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
  type: 'F1A',
  quantity: 1,
  zona: 'E',
  color_id: '',
  col2_id: '',
  tapp: false,
  cas: false,
  alt: '',
  lar: '',
  scuri: '',
  pers: '',
  zanza: '',
  allar: '',
  nomeInfisso: '',
  photos: []
});

export function usePreventivo(id, nomeRilevatore) {
  const [cliente, setCliente] = useState(null);
  const [windows, setWindows] = useState([]);
  const [extraCosts, setExtraCosts] = useState([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState(null);

  useEffect(() => {
    const ac = new AbortController();

    // ── Caso nuovo preventivo ───────────────────────────
    if (!id) {
      setCliente({
        nome: '',
        localita: '',
        telefono: '',
        note: '',
        data_creazione: '',
        basic: 0,
        target: 0,
        evoplus: 0,
        rilevatore: nomeRilevatore || '',
        nome_rilevatore: ''
      });
      setWindows([INITIAL_WINDOW()]);
      setExtraCosts([{ description: '', amount: 0 }]);
      setLoading(false);
      setError(null);
      return () => ac.abort();
    }

    // ── Caso modifica preventivo ────────────────────────
    setLoading(true);
    setError(null);

    fetch(`/preventivi/get_preventivo.php?id=${encodeURIComponent(id)}`, {
      signal: ac.signal
    })
      .then(async (res) => {
        const text = await res.text();
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);

        let json;
        try {
          json = JSON.parse(text);
        } catch {
          throw new Error('Risposta non valida dal server (JSON)');
        }

        if (json.status !== 'OK') throw new Error(json.message || 'Errore nel recupero preventivo');

        // ── Cliente ─────────────────────────────────────
        const c = json.cliente || {};
        const clienteCaricato = {
          id: Number(c.id) || Number(id) || 0,
          nome: c.nome || '',
          localita: c.localita || '',
          telefono: c.telefono || '',
          note: c.note || '',
          data_creazione: c.data_creazione || '',
          basic: Number(c.basic) || 0,
          target: Number(c.target) || 0,
          evoplus: Number(c.evoplus) || 0,
          rilevatore: c.rilevatore ?? (nomeRilevatore || ''),
          nome_rilevatore: c.nome_rilevatore || ''
        };
        if (!ac.signal.aborted) setCliente(clienteCaricato);

        // ── Finestre ─────────────────────────────────────
        const win = Array.isArray(json.windows) ? json.windows : [];
        const parsedWindows = win.map((w) => {
          let photos = [];
          if (Array.isArray(w.photos)) photos = w.photos;
          else if (typeof w.photos === 'string' && w.photos.trim() !== '') {
            try {
              const tmp = JSON.parse(w.photos);
              photos = Array.isArray(tmp) ? tmp : [];
            } catch {
              photos = [];
            }
          }
          return {
            id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
            type: w.tipo || 'F1A',
            quantity: parseInt(w.quantita ?? w.quantity ?? 1, 10) || 1,
            zona: w.zona || 'E',
            color_id: w.color_id != null && w.color_id !== '' ? String(w.color_id) : '',
            col2_id: w.col2_id != null && w.col2_id !== '' ? String(w.col2_id) : '',
            tapp: Boolean(Number(w.tapp || 0)),
            cas: Boolean(Number(w.cas || 0)),
            alt: String(w.alt ?? ''),
            lar: String(w.lar ?? ''),
            scuri: String(w.scuri ?? ''),
            pers: String(w.pers ?? ''),
            zanza: String(w.zanza ?? ''),
            allar: String(w.allar ?? ''),
            nomeInfisso: String(w.nome_infisso ?? ''),
            photos
          };
        });
        if (!ac.signal.aborted) setWindows(parsedWindows);

        // ── Costi extra ──────────────────────────────────
        const extras = Array.isArray(json.extraCosts) ? json.extraCosts : [];
        const parsedExtras = extras.map((c) => ({
          description: c.descrizione || c.description || '',
          amount: Number(c.importo ?? c.amount ?? 0) || 0
        }));
        if (!ac.signal.aborted) setExtraCosts(parsedExtras);
      })
      .catch((err) => {
        if (!ac.signal.aborted) {
          console.error('[usePreventivo] get_preventivo:', err);
          setError(err.message);
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [id, nomeRilevatore]);

  return {
    cliente,
    windows,
    extraCosts,
    setCliente,
    setWindows,
    setExtraCosts,
    loading,
    error
  };
}
